import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { getMonthOptions, currentMonth, getDateRangeForMonth, formatNum } from '../../lib/month';
import { useMdr } from '../../hooks/useMdr';

interface SubShare {
  subMdrId: string;
  subMdrName: string;
  payinFeePercent: number;
  payoutFeePercent: number;
  payinAmount: number;
  payoutAmount: number;
}

interface StatsResponse {
  items: Array<{
    id: string;
    date: string;
    deposit: number;
    topup: number;
    withdraw: number;
    settle: number;
    hold: number;
  }>;
  total: number;
  sharing: {
    merchantId: string;
    merchantName: string;
    payinVolume: number;
    payoutVolume: number;
    masterPayinFeePercent: number;
    masterPayoutFeePercent: number;
    masterPayinFeeAmount: number;
    masterPayoutFeeAmount: number;
    masterPayinMainShare: number;
    masterPayoutMainShare: number;
    subShares: SubShare[];
  } | null;
  merchant: { id: string; code: string; name: string };
}

export function MdrMerchantDetail() {
  const { t } = useTranslation();
  const { merchantId } = useParams<{ merchantId: string }>();
  const navigate = useNavigate();
  const { isMaster } = useMdr();
  const queryClient = useQueryClient();

  const [month, setMonth] = useState(currentMonth());
  const monthOptions = getMonthOptions();
  const { dateFrom, dateTo } = getDateRangeForMonth(month);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['mdr', 'merchant-stats', merchantId, dateFrom, dateTo],
    queryFn: () =>
      apiGet<StatsResponse>(`/mdr/merchants/${merchantId}/stats`, {
        dateFrom,
        dateTo,
        granularity: 'daily',
        pageSize: '100',
      }),
    enabled: !!merchantId,
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      apiPost<{ synced: number; errors: string[] }>(`/mdr/merchants/${merchantId}/sync`, {
        queryParams: { date: new Date(`${month}-01T00:00:00.000+07:00`).toISOString() },
      }),
    onSuccess: (res) => {
      if (res.ok) {
        setSyncResult(`${t('admin.syncStats')}: ${res.data.synced}`);
        queryClient.invalidateQueries({ queryKey: ['mdr', 'merchant-stats', merchantId] });
      } else {
        setSyncResult(res.error.message);
      }
    },
  });

  const d = data?.ok ? data.data : null;
  const stats = d?.items ?? [];
  const sharing = d?.sharing ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/mdr/merchants')}>
          ← {t('mdr.merchantsTitle')}
        </Button>
        {d && (
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              {d.merchant.name}
            </h1>
            <p className="font-mono text-sm text-gray-500">{d.merchant.code}</p>
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle>{t('transactions.title')}</CardTitle>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                {dateFrom} ~ {dateTo}
              </span>
              {isMaster && (
                <Button
                  size="sm"
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                >
                  {syncMutation.isPending ? t('common.loading') : t('admin.syncStats')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {syncResult && (
            <div className="px-6 pb-3 text-xs text-gray-500">{syncResult}</div>
          )}
          {isLoading && <div className="p-4 text-gray-400">{t('common.loading')}</div>}
          {!isLoading && stats.length === 0 && (
            <div className="p-4 text-gray-400">{t('common.noData')}</div>
          )}
          {stats.length > 0 && (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('transactions.date')}</TableHead>
                    <TableHead className="text-right">{t('transactions.deposit')}</TableHead>
                    <TableHead className="text-right">{t('transactions.topup')}</TableHead>
                    <TableHead className="text-right">{t('transactions.withdraw')}</TableHead>
                    <TableHead className="text-right">{t('transactions.settle')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.map((s) => (
                    <TableRow key={s.id}>
                      <TableCell>{s.date}</TableCell>
                      <TableCell className="text-right">{formatNum(s.deposit)}</TableCell>
                      <TableCell className="text-right">{formatNum(s.topup)}</TableCell>
                      <TableCell className="text-right">{formatNum(s.withdraw)}</TableCell>
                      <TableCell className="text-right">{formatNum(s.settle)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {sharing && (
        <Card>
          <CardHeader>
            <CardTitle>{t('mdr.sharingTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950">
                <div className="text-xs font-medium text-blue-600">
                  {t('mdr.masterFee')} — {t('transactions.calcPayinFee')} (
                  {sharing.masterPayinFeePercent}%)
                </div>
                <div className="mt-1 text-xl font-bold text-blue-700 dark:text-blue-300">
                  {formatNum(sharing.masterPayinFeeAmount)}
                </div>
              </div>
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 dark:border-green-900 dark:bg-green-950">
                <div className="text-xs font-medium text-green-600">
                  {t('mdr.masterFee')} — {t('transactions.calcPayoutFee')} (
                  {sharing.masterPayoutFeePercent}%)
                </div>
                <div className="mt-1 text-xl font-bold text-green-700 dark:text-green-300">
                  {formatNum(sharing.masterPayoutFeeAmount)}
                </div>
              </div>
            </div>

            {sharing.subShares.length > 0 && (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('mdr.subMdr')}</TableHead>
                      <TableHead className="text-right">{t('mdr.payinFee')}</TableHead>
                      <TableHead className="text-right">{t('transactions.calcPayinFee')}</TableHead>
                      <TableHead className="text-right">{t('mdr.payoutFee')}</TableHead>
                      <TableHead className="text-right">
                        {t('transactions.calcPayoutFee')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sharing.subShares.map((s) => (
                      <TableRow key={s.subMdrId}>
                        <TableCell>{s.subMdrName}</TableCell>
                        <TableCell className="text-right">{s.payinFeePercent}%</TableCell>
                        <TableCell className="text-right text-blue-600">
                          {formatNum(s.payinAmount)}
                        </TableCell>
                        <TableCell className="text-right">{s.payoutFeePercent}%</TableCell>
                        <TableCell className="text-right text-green-600">
                          {formatNum(s.payoutAmount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {isMaster && (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="text-gray-600 dark:text-gray-300">
                  {t('mdr.masterRemainder')} ({t('transactions.calcPayinFee')}):{' '}
                  <span className="font-bold">{formatNum(sharing.masterPayinMainShare)}</span>
                </div>
                <div className="text-gray-600 dark:text-gray-300">
                  {t('mdr.masterRemainder')} ({t('transactions.calcPayoutFee')}):{' '}
                  <span className="font-bold">{formatNum(sharing.masterPayoutMainShare)}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
